import { expandSemanticText, semanticConcepts } from './search-language.js';

export const INDEX_MAGIC = 'PTSI';
export const INDEX_VERSION = 1;
export const INDEX_HEADER_BYTES = 32;

const align4 = (value) => (value + 3) & ~3;

export function decodeIndex(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  const magic = new TextDecoder().decode(bytes.subarray(0, 4));
  const view = new DataView(arrayBuffer);
  const version = view.getUint16(4, true);
  const headerBytes = view.getUint16(6, true);
  const dimensions = view.getUint16(8, true);
  const vectorsPerSite = view.getUint16(10, true);
  const count = view.getUint32(12, true);
  const metadataBytes = view.getUint32(16, true);
  const vectorBytes = view.getUint32(20, true);

  if (magic !== INDEX_MAGIC) throw new Error(`未知索引格式：${magic}`);
  if (version !== INDEX_VERSION) throw new Error(`不支持的索引版本：${version}`);
  if (headerBytes !== INDEX_HEADER_BYTES) throw new Error('索引头长度不匹配');

  const metadataStart = headerBytes;
  const metadataEnd = metadataStart + metadataBytes;
  const vectorStart = align4(metadataEnd);
  const expectedVectorBytes = count * vectorsPerSite * dimensions * Float32Array.BYTES_PER_ELEMENT;
  if (vectorBytes !== expectedVectorBytes || vectorStart + vectorBytes !== arrayBuffer.byteLength) {
    throw new Error('向量数据长度不匹配');
  }

  const metadata = JSON.parse(new TextDecoder().decode(bytes.subarray(metadataStart, metadataEnd)));
  if (!Array.isArray(metadata.sites) || metadata.sites.length !== count) {
    throw new Error('站点元数据数量不匹配');
  }

  return {
    ...metadata,
    count,
    dimensions,
    vectorsPerSite,
    vectors: new Float32Array(arrayBuffer, vectorStart, vectorBytes / 4),
  };
}

export function normalizeSearchText(value) {
  return String(value ?? '')
    .normalize('NFKC')
    .toLocaleLowerCase('zh-CN')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

function isSafePartialName(value) {
  const compact = value.replace(/\s+/g, '');
  const length = [...compact].length;
  return /\p{Script=Han}/u.test(compact) ? length >= 2 : length >= 3;
}

function strictIntentFilter(rawQuery) {
  const query = normalizeSearchText(rawQuery);
  const communityLabels = new Map([
    ['十二大', '十二大'],
    ['top 12 chinese trackers', '十二大'],
    ['传统九大', '传统九大'],
    ['traditional nine', '传统九大'],
    ['外站影视三强', '外站影视三强'],
    ['top movie tv trackers', '外站影视三强'],
    ['皮妞堡', '皮妞堡'],
  ]);
  const communityLabel = communityLabels.get(query);
  if (communityLabel) return (site) => site.community_labels.includes(communityLabel);

  if (query === '开放注册' || query === 'open signup') return (site) => site.registration === '开放注册';
  if (query === '开放邀请' || query === 'invites available') return (site) => site.registration === '开放邀请';
  if (query === '邀请制' || query === 'invite only') {
    return (site) => site.registration.includes('邀请') && !site.registration.includes('跑路');
  }
  if (['高校教育网', 'ipv6 高校站', 'university ipv6'].includes(query)) {
    return (site) => site.network_scope === 'education_network'
      || site.organization_type === 'university'
      || site.organization_type === 'university_origin'
      || site.community_labels.includes('高校站');
  }
  if (query === '华语社区' || query === 'chinese language trackers') return (site) => site.pt_circle === 'inner';
  if (query === '海外社区' || query === 'international trackers') return (site) => site.pt_circle === 'outer';
  if (['pt 交流论坛', 'nas 社区', 'pt community forums', 'nas community'].includes(query)) {
    return (site) => site.service_type === 'community_forum';
  }
  return null;
}

function lexicalMatch(site, rawQuery) {
  const query = normalizeSearchText(rawQuery);
  if (!query) return { score: 0, kind: null };

  const names = [site.name, site.source_name, ...(site.aka ?? [])].map(normalizeSearchText).filter(Boolean);
  if (names.some((name) => name === query)) return { score: 1, kind: 'name' };
  if (names.some((name) => name.startsWith(query) || (isSafePartialName(name) && query.startsWith(name)))) {
    return { score: 0.86, kind: 'text' };
  }
  if (names.some((name) => name.includes(query) || (isSafePartialName(name) && query.includes(name)))) {
    return { score: 0.72, kind: 'text' };
  }

  const circleTerms = site.pt_circle === 'inner'
    ? 'inner 内站 华语社区 Chinese-language trackers'
    : site.pt_circle === 'outer' ? 'outer 外站 海外社区 International trackers' : '';
  const serviceTerms = site.service_type === 'community_forum' ? 'PT 交流论坛 PT community forums NAS 社区 NAS community' : '';
  const networkTerms = site.network_scope === 'education_network'
    || site.organization_type === 'university'
    || site.organization_type === 'university_origin'
    ? '高校 教育网 IPv6 University campus' : '';
  const structuredValues = [
    ...(site.content_types ?? []),
    ...(site.resource_categories ?? []),
    ...(site.community_labels ?? []),
    ...(site.specialties ?? []),
    ...(site.official_groups ?? []),
    ...(site.tags ?? []),
    site.registration,
    site.difficulty,
    site.source_category,
    site.network_scope,
    site.organization_type,
    site.language,
    circleTerms,
    serviceTerms,
    networkTerms,
  ].map(normalizeSearchText).filter(Boolean);
  if (structuredValues.some((value) => value === query)) return { score: 0.94, kind: 'field' };
  if (structuredValues.some((value) => value.includes(query) || (isSafePartialName(value) && query.includes(value)))) {
    return { score: 0.78, kind: 'field' };
  }

  const queryConcepts = semanticConcepts(rawQuery);
  if (queryConcepts.length) {
    const structuredConcepts = new Set(semanticConcepts(structuredValues.join(' ')));
    const matchedConcepts = queryConcepts.filter((concept) => structuredConcepts.has(concept)).length;
    if (matchedConcepts) return { score: 0.48 + (matchedConcepts / queryConcepts.length) * 0.32, kind: 'field' };
  }

  const searchable = normalizeSearchText([
    site.name,
    site.source_name,
    ...(site.aka ?? []),
    ...structuredValues,
    site.beginner_tips,
    site.service_type,
    site.features,
  ].join(' '));
  if (searchable.includes(query)) return { score: 0.58, kind: 'text' };

  const tokens = query.split(/\s+/).filter(Boolean);
  if (tokens.length && tokens.every((token) => searchable.includes(token))) return { score: 0.42, kind: 'text' };

  if (queryConcepts.length) {
    const siteConcepts = new Set(semanticConcepts(searchable));
    const matchedConcepts = queryConcepts.filter((concept) => siteConcepts.has(concept)).length;
    if (matchedConcepts) return { score: 0.24 + (matchedConcepts / queryConcepts.length) * 0.46, kind: 'text' };
  }
  return { score: 0, kind: null };
}

export function searchIndex(index, query, embed, { limit = index.count } = {}) {
  // Accept this homophone typo as a query correction, without making it a site alias.
  const trimmedQuery = query.trim();
  const value = normalizeSearchText(trimmedQuery) === '蝶粉' ? '碟粉' : trimmedQuery;
  if (!value) return index.sites.map((site, i) => ({ site, index: i, score: 0, semantic: 0, lexical: 0, matchKind: null }));

  const semanticEnabled = typeof embed === 'function';
  const intentFilter = strictIntentFilter(value);
  const queryVector = semanticEnabled ? embed(expandSemanticText(value)) : null;
  if (queryVector && queryVector.length !== index.dimensions) throw new Error('查询向量维度与索引不一致');

  const scored = index.sites.map((site, siteIndex) => {
    let semantic = 0;
    if (queryVector) {
      semantic = -2;
      for (let vectorIndex = 0; vectorIndex < index.vectorsPerSite; vectorIndex += 1) {
        const offset = (siteIndex * index.vectorsPerSite + vectorIndex) * index.dimensions;
        let dot = 0;
        for (let d = 0; d < index.dimensions; d += 1) dot += queryVector[d] * index.vectors[offset + d];
        if (dot > semantic) semantic = dot;
      }
    }
    const lexicalMatchResult = lexicalMatch(site, value);
    const lexical = lexicalMatchResult.score;
    const exactMatchBoost = lexicalMatchResult.kind === 'name' ? 3 : 0;
    const fieldMatchBoost = lexicalMatchResult.kind === 'field'
      ? (lexical >= 0.9 ? 0.9 : 0.8)
      : 0;
    const score = semanticEnabled ? exactMatchBoost + fieldMatchBoost + semantic * 0.76 + lexical * 0.64 : lexical;
    return { site, index: siteIndex, semantic, lexical, score, matchKind: lexicalMatchResult.kind };
  }).filter(({ site }) => !intentFilter || intentFilter(site));

  return scored.sort((a, b) => b.score - a.score).slice(0, limit);
}

export function sortDirectory(results) {
  const sourceOrder = new Map([
    ['热门站点', 0],
    ['老牌站点', 1],
    ['新兴站点', 2],
    ['其他站点', 3],
    ['教育站点', 4],
    ['海外站点', 5],
    ['用户补充', 6],
  ]);
  return [...results].sort((a, b) => {
    const sourceDiff = (sourceOrder.get(a.site.source_category) ?? 99) - (sourceOrder.get(b.site.source_category) ?? 99);
    if (sourceDiff) return sourceDiff;
    const rankDiff = (a.site.rank ?? Number.MAX_SAFE_INTEGER) - (b.site.rank ?? Number.MAX_SAFE_INTEGER);
    return rankDiff || a.site.name.localeCompare(b.site.name, 'zh-CN');
  });
}
